import {
  type UseQueryOptions,
  type UseQueryResult,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { BOARD_CONFIG } from "@/config/browserBoard";
import type { ArchiveStorage } from "@/features/archive/types";
import { apiGet } from "@/shared/api";

const MAX_CONCURRENT_ARCHIVE_STORAGE_REQUESTS = 4;

type ArchiveStorageRequestWaiter = {
  readonly signal: AbortSignal;
  readonly resolve: (release: () => void) => void;
  readonly reject: (reason: unknown) => void;
  readonly onAbort: () => void;
};

let activeArchiveStorageRequests = 0;
const pendingArchiveStorageRequests: ArchiveStorageRequestWaiter[] = [];

function createArchiveStorageRequestRelease(): () => void {
  let released = false;
  return (): void => {
    if (released) return;
    released = true;
    activeArchiveStorageRequests -= 1;

    while (
      activeArchiveStorageRequests < MAX_CONCURRENT_ARCHIVE_STORAGE_REQUESTS
    ) {
      const waiter = pendingArchiveStorageRequests.shift();
      if (!waiter) return;
      waiter.signal.removeEventListener("abort", waiter.onAbort);
      if (waiter.signal.aborted) {
        waiter.reject(waiter.signal.reason);
        continue;
      }
      activeArchiveStorageRequests += 1;
      waiter.resolve(createArchiveStorageRequestRelease());
    }
  };
}

function acquireArchiveStorageRequestSlot(
  signal: AbortSignal,
): Promise<() => void> {
  signal.throwIfAborted();
  if (activeArchiveStorageRequests < MAX_CONCURRENT_ARCHIVE_STORAGE_REQUESTS) {
    activeArchiveStorageRequests += 1;
    return Promise.resolve(createArchiveStorageRequestRelease());
  }

  return new Promise((resolve, reject) => {
    let waiter: ArchiveStorageRequestWaiter;
    const onAbort = (): void => {
      const index = pendingArchiveStorageRequests.indexOf(waiter);
      if (index === -1) return;
      pendingArchiveStorageRequests.splice(index, 1);
      signal.removeEventListener("abort", onAbort);
      reject(signal.reason);
    };
    waiter = { signal, resolve, reject, onAbort };
    pendingArchiveStorageRequests.push(waiter);
    signal.addEventListener("abort", onAbort, { once: true });
    if (signal.aborted) onAbort();
  });
}

async function withArchiveStorageRequestSlot<T>(
  signal: AbortSignal,
  request: () => Promise<T>,
): Promise<T> {
  const release = await acquireArchiveStorageRequestSlot(signal);
  try {
    signal.throwIfAborted();
    return await request();
  } finally {
    release();
  }
}

export function getArchiveStoragePath(slug: string): string {
  return `/archive/${encodeURIComponent(slug)}/storage`;
}

function getArchiveStorageQueryOptions(
  slug: string,
  enabled: boolean,
  revision: number = 0,
): UseQueryOptions<ArchiveStorage> {
  const path = getArchiveStoragePath(slug);
  return {
    queryKey: ["archive-storage", path, revision],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      withArchiveStorageRequestSlot(signal, () =>
        apiGet<ArchiveStorage>(path, { boardKey: slug, signal }),
      ),
    refetchOnWindowFocus: true,
    enabled,
  } as const;
}

export function useArchiveStorage(
  enabled: boolean = true,
): UseQueryResult<ArchiveStorage> {
  return useQuery(
    getArchiveStorageQueryOptions(BOARD_CONFIG.boardKey, enabled),
  );
}

/** 総合過去ログで選択中の各板について、板別容量を同時に取得する。 */
export function useArchiveStorages(
  slugs: readonly string[],
  enabled: boolean = true,
  revision: number = 0,
): UseQueryResult<ArchiveStorage>[] {
  return useQueries({
    queries: slugs.map((slug) =>
      getArchiveStorageQueryOptions(slug, enabled, revision),
    ),
  });
}
